import React, { useEffect, useState, useCallback, useRef } from "react";
import { message, Modal, Table } from "antd";
import Text from "../../components/Text/Text";
import icon from "../../resource/icon";
import Button from "../../components/Button/Button";
import { MinusCircleOutlined } from "@ant-design/icons";
import { color } from "../../resource";
import { roleDatasource } from "../../datasource/RoleDatasource";
import { useLocalStorage } from "../../hook/useLocalStorage";
import { deleteApprover } from "../../datasource/OverCreditApproveDatasource";
import { v4 as uuidv4 } from "uuid";
import styled from "styled-components";
import Select from "../../components/Select/Select";
interface AntSelectCustomProps {
  error?: boolean;
}

const AntSelectCustom = styled(Select)<AntSelectCustomProps>`
  .ant-select-selector {
    height: 100% !important;
    min-height: 40px !important;
    display: flex;
    align-items: center;
    font-family: Sarabun !important;
    border: 1px solid ${({ error }) => (error ? "red" : "rgba(0,0,0,0.15)")} !important;
    box-shadow: ${({ error }) => (error ? `${color.error}` : "none")};
  }
  .ant-select-selection-placeholder {
    font-family: Sarabun !important;
  }

  .ant-select-selection-item {
    border-radius: 4px;
    font-family: Sarabun !important;
    font-weight: 600 !important;
    display: flex;
    align-items: center;
  }
  .ant-select-selection-wrap {
    align-self: center !important;
  }
`;

interface ApproverUser {
  label: string;
  value: string;
  key: string;
  firstname: string;
  lastname: string;
  roleId: string;
}

interface ApproverRow {
  key: string;
  user?: ApproverUser | null;
  error?: string;
}

interface FormattedApprover {
  sequenceId: string;
  overCreditApproverId?: string;
  roleId: string;
  rolename: string;
  userId: string;
  firstname: string;
  lastname: string;
  number: number;
}

interface Props {
  approverCount: number;
  roleName: string;
  sequenceId: string;
  overCreditApproverIds: FormattedApprover[];
  callBackDataUser: (formatted: FormattedApprover[]) => void;
  formTable: any;
  index: number;
  err: any;
}

const CustomPersonInputList: React.FC<Props> = ({
  approverCount,
  roleName,
  sequenceId,
  overCreditApproverIds,
  callBackDataUser,
  formTable,
  index,
  err,
}) => {
  const [company] = useLocalStorage("profile", null);
  const updateBy = `${company?.firstname} ${company?.lastname}`;
  const [userApprove, setUserApprove] = useState<ApproverUser[]>([]);
  const [dataSource, setDataSource] = useState<ApproverRow[]>([]);
  const [errorMessages, setErrorMessages] = useState<{ [key: string]: string }>({});
  const prevFormattedRef = useRef<FormattedApprover[]>([]);

  const fetchUserApprovers = useCallback(async () => {
    if (!company?.company || !roleName) return;
    try {
      const res = await roleDatasource.userInRole(company.company, roleName, "ACTIVE");
      const formatted: ApproverUser[] = res.map((user: any) => ({
        label: `${user.firstname} ${user.lastname}`,
        value: user.userStaffId,
        key: user.userStaffId,
        firstname: user.firstname,
        lastname: user.lastname,
        roleId: user.roleId,
      }));
      setUserApprove(formatted);
    } catch (error) {
      console.error("Failed to fetch approvers", error);
    }
  }, [company?.company, roleName]);

  const mapInitialDataSource = useCallback((): ApproverRow[] => {
    const conditions: any[] = formTable.getFieldValue("conditions") || [];
    const targetCondition = conditions[index];
    const isAssign = targetCondition?.condition === "ASSIGN";
    const approvers = targetCondition?.approvers || [];
    const count = isAssign
      ? approvers.length
      : Math.max(approverCount, overCreditApproverIds?.length || 0);

    return Array.from({ length: count }, (_, i) => {
      const a = overCreditApproverIds?.[i];
      return {
        key: uuidv4(),
        user: a
          ? {
              label: `${a.firstname} ${a.lastname}`,
              value: a.userId,
              key: a.userId,
              firstname: a.firstname,
              lastname: a.lastname,
              roleId: a.roleId,
            }
          : {
              label: "",
              value: "",
              key: "",
              firstname: "",
              lastname: "",
              roleId: "",
            },
      };
    });
  }, [approverCount, overCreditApproverIds, formTable, index]);

  useEffect(() => {
    fetchUserApprovers();
  }, [fetchUserApprovers]);

  useEffect(() => {
    const conditions: any[] = formTable.getFieldValue("conditions") || [];
    const targetCondition = conditions[index];
    const isAssign = targetCondition?.condition === "ASSIGN";
    const approvers = targetCondition?.approvers || [];

    const expectedCount = isAssign
      ? approvers.length
      : Math.max(approverCount, overCreditApproverIds?.length || 0);

    if (dataSource.length !== expectedCount) {
      setDataSource(mapInitialDataSource());
    }
  }, [approverCount, overCreditApproverIds, formTable, index, mapInitialDataSource]);

  const validateDataSource = (data: typeof dataSource) => {
    const seenKeys = new Set<string>();
    const newErrors = data.reduce(
      (acc, row) => {
        const k = row.user?.key;
        if (!k) {
          acc[row.key] = "เลือกชื่อผู้อนุมัติ";
        } else if (seenKeys.has(k)) {
          acc[row.key] = "ชื่อผู้อนุมัติซ้ำกัน";
        } else {
          seenKeys.add(k);
          acc[row.key] = "";
        }
        return acc;
      },
      {} as Record<string, string>,
    );
    setErrorMessages(newErrors);
    err(Object.values(newErrors).some((msg) => msg !== ""));
  };

  const handleSelectChange = (value: string, rowKey: string) => {
    const selectedUser = userApprove.find((u) => u.key === value);

    setDataSource((prev) => {
      const newData = prev.map((row) =>
        row.key === rowKey ? { ...row, user: selectedUser } : row,
      );

      const seenKeys = new Set<string>();
      const newErrors = newData.reduce(
        (acc, row) => {
          const k = row.user?.key;
          if (!k) {
            acc[row.key] = row.key === rowKey ? "เลือกชื่อผู้อนุมัติ" : "";
          } else if (seenKeys.has(k)) {
            acc[row.key] = "ชื่อผู้อนุมัติซ้ำกัน";
          } else {
            seenKeys.add(k);
            acc[row.key] = "";
          }
          return acc;
        },
        {} as Record<string, string>,
      );
      setErrorMessages(newErrors);
      const hasError = Object.values(newErrors).some((msg) => msg !== "");
      err(hasError);
      return newData;
    });
  };
  const handleDelete = (keyToRemove: string) => {
    const matchedRow = dataSource.find((row) => row.key === keyToRemove);
    if (!matchedRow?.key) return;
    const foundApprover = overCreditApproverIds.find((a) => a.userId === matchedRow.user?.key);
    console.log(keyToRemove);

    const updateDataSource = (prev: typeof dataSource) => {
      const newData = prev.filter((row) => row.key !== keyToRemove);
      validateDataSource(newData);
      return newData;
    };

    if (!foundApprover?.overCreditApproverId) {
      setDataSource(updateDataSource);
      return;
    }

    Modal.confirm({
      title: "ยืนยันการลบ",
      content: "คุณต้องการลบผู้อนุมัติคนนี้หรือไม่?",
      okText: "ยืนยัน",
      cancelText: "ยกเลิก",
      onOk: async () => {
        try {
          await deleteApprover(foundApprover.overCreditApproverId!, updateBy);
          setDataSource(updateDataSource);
          message.success("ลบผู้อนุมัติเรียบร้อยแล้ว");
        } catch (error) {
          console.error("Delete failed", error);
          message.error("เกิดข้อผิดพลาดในการลบ");
        }
      },
    });
  };

  const addRow = () => {
    setDataSource((prev) => [
      ...prev,
      {
        key: uuidv4(),
        user: {
          label: "",
          value: "",
          key: "",
          firstname: "",
          lastname: "",
          roleId: "",
        },
      },
    ]);
  };

  useEffect(() => {
    const formatted: FormattedApprover[] = dataSource
      .map((row, idx) =>
        row.user
          ? {
              ...(overCreditApproverIds?.[idx]?.overCreditApproverId
                ? { overCreditApproverId: overCreditApproverIds[idx].overCreditApproverId }
                : {}),
              sequenceId: sequenceId || undefined,
              rolename: roleName,
              roleId: row.user.roleId,
              userId: row.user.key,
              firstname: row.user.firstname,
              lastname: row.user.lastname,
              number: idx + 1,
            }
          : null,
      )
      .filter(Boolean) as FormattedApprover[];
    const isEqual = JSON.stringify(formatted) === JSON.stringify(prevFormattedRef.current);
    if (!isEqual) {
      prevFormattedRef.current = formatted;
      callBackDataUser(formatted);
      formTable.setFields([
        {
          name: ["conditions", index, "approvers"],
          value: formatted,
        },
      ]);
    }
  }, [dataSource]);

  const columns = [
    {
      title: "",
      dataIndex: "gap",
      key: "gap",
      width: "12%",
    },
    {
      title: "ผู้อนุมัติ",
      dataIndex: "approver",
      key: "approver",
      width: "100%",
      render: (_: any, __: any, idx: number) => (
        <>
          <img
            src={icon.icons_down}
            alt='icon'
            style={{ width: 22, height: 20, marginRight: 10 }}
          />
          <Text fontFamily='Sarabun' fontSize={14}>{`ผู้อนุมัติคนที่ ${idx + 1}`}</Text>
        </>
      ),
    },
    {
      title: "เลือกชื่อผู้อนุมัติ",
      dataIndex: "select",
      key: "select",
      render: (_: any, record: ApproverRow, idx: number) => {
        const error = errorMessages[record.key];
        return (
          <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, width: " 100%" }}>
              <AntSelectCustom
                placeholder='เลือกชื่อผู้อนุมัติ'
                data={userApprove}
                value={record.user?.key || undefined}
                onChange={(value) => {
                  handleSelectChange(value, record.key);
                }}
                error={!!errorMessages[record.key]}
              />
              {idx >= approverCount && (
                <MinusCircleOutlined
                  style={{ fontSize: 20, cursor: "pointer", color: color.Grey }}
                  onClick={() => handleDelete(record.key)}
                />
              )}
            </div>
            {error && (
              <div style={{ color: color.error, fontSize: 14, marginLeft: 4, marginTop: 8 }}>
                {error}
              </div>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div style={{ margin: "0px -16px 0px 84px" }}>
      <Table
        showHeader={false}
        dataSource={dataSource}
        columns={columns}
        pagination={false}
        rowKey='key'
      />
      <div style={{ backgroundColor: "#f3f6f9", padding: 12, marginRight: "16px" }}>
        <Button onClick={addRow} typeButton='light-dashed' title='เพิ่มผู้อนุมัติ' />
      </div>
    </div>
  );
};

export default CustomPersonInputList;
